// Read-only registry of every scanner the platform knows about.
// Tier 0: every row is `enabled = false`; the page exists so admins
// can see what's coming and what's currently dispatched-but-skipped.

import { ScanLine, Cpu } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { useScanners } from '../../api/detection';

function costClassTone(costClass: string): 'success' | 'accent' | 'warning' {
  if (costClass === 'cheap') return 'success';
  if (costClass === 'medium') return 'accent';
  return 'warning';
}

export function DetectionScanners() {
  const { data: scanners, isLoading } = useScanners();
  const enabled = (scanners ?? []).filter((s) => s.enabled);
  const disabled = (scanners ?? []).filter((s) => !s.enabled);

  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <p className="orb-eyebrow text-accent/80 mb-2">Detection</p>
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-accent/10 border border-accent/30 flex items-center justify-center shrink-0">
              <Cpu className="h-5 w-5 text-accent" />
            </div>
            <div>
              <h1 className="text-2xl font-display font-semibold text-fg leading-tight">Scanners</h1>
              <p className="text-fg-muted text-sm mt-2 max-w-2xl">
                Every scanner the platform registers. Enabled scanners run
                automatically against new uploads; disabled scanners are
                visible but inert.
              </p>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader
            title={`Enabled (${enabled.length})`}
            actions={isLoading ? <Spinner size="xs" /> : null}
          />
          <CardBody>
            {enabled.length === 0 ? (
              <div className="text-center py-8 text-fg-muted">
                <ScanLine className="h-8 w-8 mx-auto mb-2 text-fg-subtle" />
                <p className="text-sm">No scanners are enabled in this build.</p>
                <p className="text-xs mt-1">
                  Tier 1 will ship the first two (signature + SBOM extract).
                </p>
              </div>
            ) : (
              <ScannerTable scanners={enabled} />
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title={`Disabled (${disabled.length})`} description="Registered but not currently executed." />
          <CardBody>
            {disabled.length === 0 ? (
              <p className="text-sm text-fg-muted">None.</p>
            ) : (
              <ScannerTable scanners={disabled} />
            )}
          </CardBody>
        </Card>
      </div>
    </AppShell>
  );
}

function ScannerTable({ scanners }: Readonly<{ scanners: { id: string; kind: string; version: string; rules_version: string; description: string; cost_class: string }[] }>) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left border-b border-border">
          <th className="py-2 pr-3 font-medium text-fg-muted">ID</th>
          <th className="py-2 pr-3 font-medium text-fg-muted">Kind</th>
          <th className="py-2 pr-3 font-medium text-fg-muted">Version</th>
          <th className="py-2 pr-3 font-medium text-fg-muted">Rules</th>
          <th className="py-2 pr-3 font-medium text-fg-muted">Cost</th>
        </tr>
      </thead>
      <tbody>
        {scanners.map((s) => (
          <tr key={s.id} className="border-b border-border/40 last:border-0">
            <td className="py-2 pr-3 font-mono text-xs text-accent">{s.id}</td>
            <td className="py-2 pr-3 text-fg-muted text-xs">{s.kind}</td>
            <td className="py-2 pr-3 text-fg-muted text-xs">{s.version}</td>
            <td className="py-2 pr-3 text-fg-muted text-xs">{s.rules_version || '—'}</td>
            <td className="py-2 pr-3">
              <Badge tone={costClassTone(s.cost_class)}>
                {s.cost_class}
              </Badge>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
